import { inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { LegacyDataMigrationService } from '../../../services/legacy-data-migration/legacy-data-migration.service';
import { getUuidV4 } from '../../../utilities/generic-utilities';
import {
    hydrateSlice,
    persistenceError,
    persistSlice,
    sliceHydrated,
    slicePersisted,
} from '../../persistence/persistence.actions';
import { notificationsUpdated$ } from '../../notification/notification-updates';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import {
    cleanupCompletedPreviouslySucceeded,
    confirmationsHydrated,
    initConfirmations,
    legacyConfirmationsMigrated,
    purgeStaleConfirmations,
    removeConfirmation,
    submitConfirmationRequested,
    upsertConfirmation,
} from '../actions/confirmation.actions';
import { CONFIRMATION_COLLECTION_FEATURE_KEY, ConfirmationState } from '../confirmation-collection.state';
import { selectConfirmationsSettled, selectStaleConfirmations } from '../selectors/confirmation.selector';
import { fromJsonConfirmationModel } from '../utility/from-json-confirmation-model';

/**
 * Owns the confirmation slice's storage flow, which is the generic `PERSISTED_SLICES` layer — this
 * effect only asks it to read and write.
 *
 * The whole collection is one concurrency-store entry, like every other persisted slice. That is safe
 * here because a technician works on one device at a time and a device switch uploads the current data
 * before the next device downloads it: there is never a second writer whose confirmations a blob write
 * could discard.
 *
 * Writes are driven by the actions that change the collection, listed on {@link persistChanges$}. That
 * list is the one thing here that has to be maintained by hand: an action added to the reducer and not
 * to it stores nothing.
 *
 * The read's result passes through {@link hydrateConfirmations$} on its way to the reducer, which merges
 * in whatever the legacy `StorageConfirmationService` left behind before the slice settles.
 */
@Injectable()
export class ConfirmationPersistenceEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly legacyDataMigration = inject(LegacyDataMigrationService);
    private readonly log: LogInterface = inject(LogInstanceToken);
    /** Ticks whenever the notification list changed — see `notification-updates.ts`. */
    private readonly notificationsUpdated$ = notificationsUpdated$();

    /**
     * Read the stored confirmations into the slice, once — on the `initConfirmations`
     * `ConfirmationIsLoadedGuard` dispatches the first time a route that needs the slice activates.
     * Gated on the slice not having settled yet, so a second `initConfirmations` cannot re-read over
     * what is already there.
     *
     * There is deliberately no second read when a cross-device sync lands: a technician works on one
     * device at a time, so picking up what another device wrote happens on the next app start, whose
     * first guarded navigation triggers this read again.
     */
    public hydrateOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initConfirmations),
            concatLatestFrom(() => this.store.select(selectConfirmationsSettled)),
            filter(([, settled]) => !settled),
            map(() => hydrateSlice({ featureKey: CONFIRMATION_COLLECTION_FEATURE_KEY })),
        ),
    );

    /**
     * Settles the slice from the layer's read, with the started confirmations the legacy
     * `StorageConfirmationService` left merged in (see {@link LegacyDataMigrationService}, which keeps
     * this inert while `enableNgRx` is off). The slice only settles once that merge is in, so the
     * confirmation route cannot open a draft that is still waiting to be migrated.
     *
     * A legacy confirmation is converted straight from its stored JSON by `fromJsonConfirmationModel` —
     * the conversion every save goes through too. Whether its notification still exists is not checked
     * here: {@link purgeOnNotificationsUpdated$} drops a migrated confirmation whose notification is gone
     * the same way it drops any other.
     *
     * When there was anything to migrate, the collection is persisted under a fresh `correlationId`, and
     * {@link deleteMigratedLegacyConfirmations$} deletes the legacy keys once that write has succeeded.
     */
    public hydrateConfirmations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === CONFIRMATION_COLLECTION_FEATURE_KEY),
            mergeMap(({ data }) =>
                this.legacyDataMigration.readLegacyConfirmations().pipe(
                    mergeMap((legacyReads) => {
                        const hydrated = confirmationsHydrated({
                            confirmations: (data as ConfirmationState[] | undefined) ?? [],
                            legacy: legacyReads.map((read) =>
                                fromJsonConfirmationModel(read.data, read.confirmationType, read.timestamp),
                            ),
                        });
                        if (legacyReads.length === 0) {
                            return of(hydrated);
                        }
                        const keys = legacyReads.map((read) => read.key);
                        this.log.debug(
                            `ConfirmationPersistenceEffects: hydrateConfirmations$: migrating the legacy confirmations ${keys.join(', ')}`,
                        );
                        const correlationId = getUuidV4();
                        return of(
                            hydrated,
                            // Before the persist, so the key deletion is already listening when it settles.
                            legacyConfirmationsMigrated({ keys, correlationId }),
                            persistSlice({ featureKey: CONFIRMATION_COLLECTION_FEATURE_KEY, correlationId }),
                        );
                    }),
                ),
            ),
        ),
    );

    /**
     * Deletes the migrated legacy keys once the persist carrying the migrated confirmations has
     * succeeded — never earlier, so a failed persist leaves them for the next start to migrate again.
     */
    public deleteMigratedLegacyConfirmations$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(legacyConfirmationsMigrated),
                mergeMap(({ keys, correlationId }) =>
                    this.actions$.pipe(
                        ofType(slicePersisted, persistenceError),
                        filter(
                            (action) =>
                                action.featureKey === CONFIRMATION_COLLECTION_FEATURE_KEY &&
                                action.correlationId === correlationId,
                        ),
                        take(1),
                        mergeMap((action) => {
                            if (action.type === persistenceError.type) {
                                this.log.error(
                                    `ConfirmationPersistenceEffects: deleteMigratedLegacyConfirmations$: the migrated confirmations were not persisted, keeping ${keys.join(', ')}`,
                                );
                                return of(undefined);
                            }
                            return this.legacyDataMigration.deleteLegacyKeys(keys);
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    /**
     * Write the collection back to storage after every action that changes it.
     *
     * This is the set the reducer has a handler for, minus `confirmationsHydrated`: what came out of
     * storage does not need putting back, and a hydrate that migrated legacy confirmations persists
     * them itself, so that it can wait for that write before deleting the legacy keys. Effects run after the reducers, so by the time one of these arrives
     * the slice already holds what the layer should write.
     *
     * Listing the actions rather than watching the projection means an idempotent no-op — a
     * `removeConfirmation` for a confirmation that is not stored — still rewrites the collection. That
     * is a compress-and-write of a handful of confirmations, cheap enough not to buy a comparator for.
     *
     * Each of them carries a `correlationId`, and it is forwarded to the layer unchanged: the generic
     * layer echoes the id back on `slicePersisted`/`persistenceError`, so any of these writes can be
     * awaited by whoever triggered it. `saveConfirmation` is the caller that does; the rest are
     * fire-and-forget — a submit settles on its own outcome actions rather than on the write — and for
     * them the id only identifies the write in the trail.
     */
    public persistChanges$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                upsertConfirmation,
                submitConfirmationRequested,
                removeConfirmation,
                cleanupCompletedPreviouslySucceeded,
                purgeStaleConfirmations,
            ),
            map((action) =>
                persistSlice({
                    featureKey: CONFIRMATION_COLLECTION_FEATURE_KEY,
                    correlationId: action.correlationId,
                }),
            ),
        ),
    );

    /**
     * Proactively purges confirmations whose notification is no longer present. Which those are is
     * entirely {@link selectStaleConfirmations}' answer — both sides of the comparison are read off the
     * store the moment the notification list changed, so no service stands between this effect and
     * them. Removing them from the slice is enough — {@link persistChanges$} then has the layer delete
     * their storage entries.
     */
    public purgeOnNotificationsUpdated$ = createEffect(() =>
        this.notificationsUpdated$.pipe(
            concatLatestFrom(() => this.store.select(selectStaleConfirmations)),
            map(([, stale]) => stale),
            filter((stale) => stale.length > 0),
            map((stale) => purgeStaleConfirmations({ stale, correlationId: getUuidV4() })),
        ),
    );
}
